import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Alert, Upload, Button, Space, Avatar, message } from "antd";
import { UploadOutlined, UserOutlined } from "@ant-design/icons";
import useSWR from "swr";
import {
  getMyAdminAuthor,
  upsertMyAdminAuthor,
  uploadAdminAuthorImage,
} from "../../api/domainApi";
import { AdminCommentAuthor } from "../../types/domain";

/**
 * 관리자 프로필(관리자명·사진) 등록/수정.
 *
 * 여기서 정한 값이 사용자 화면의 닉네임·프로필 이미지 자리에 그대로 노출된다.
 * 수정하면 이미 작성한 댓글에도 소급 반영된다 (작성 시점 스냅샷이 아님).
 *
 * 이미지는 서버가 업로드+확정을 한 번에 처리하고 최종 URL 만 돌려준다.
 */
const AdminAuthorModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSaved?: (author: AdminCommentAuthor) => void;
}> = ({ open, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // 미등록이면 404 — 에러가 아니라 "아직 없음" 이므로 조용히 넘긴다.
  const { data, mutate } = useSWR(
    open ? "/admin/community/me/author" : null,
    () => getMyAdminAuthor().catch(() => null),
  );

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ displayName: data?.displayName ?? "" });
    setImageUrl(data?.profileImageUrl ?? null);
  }, [open, data, form]);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      message.error("이미지 파일만 업로드할 수 있습니다.");
      return false;
    }
    setUploading(true);
    try {
      const res = await uploadAdminAuthorImage(file);
      setImageUrl(res.profileImageUrl);
      message.success("이미지가 업로드되었습니다. 저장을 눌러야 반영됩니다.");
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setUploading(false);
    }
    return false; // antd 자동 업로드 사용 안 함
  };

  const submit = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      const saved = await upsertMyAdminAuthor({
        displayName: v.displayName.trim(),
        profileImageUrl: imageUrl,
      });
      message.success("프로필이 저장되었습니다.");
      mutate();
      onSaved?.(saved);
      onClose();
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="관리자 프로필"
      onCancel={onClose}
      onOk={submit}
      okText="저장"
      cancelText="닫기"
      confirmLoading={saving}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="여기서 정한 이름과 사진이 사용자 화면에 그대로 노출됩니다."
        description="이미 작성한 댓글에도 소급 반영됩니다. 반려견 정보는 표시되지 않고, 사용자가 프로필을 눌러도 카드가 열리지 않습니다."
      />
      <Form form={form} layout="vertical">
        <Form.Item
          name="displayName"
          label="관리자명"
          rules={[
            { required: true, message: "관리자명을 입력해 주세요." },
            { max: 50, message: "최대 50자입니다." },
          ]}
        >
          <Input placeholder="예: 놀로 운영자" maxLength={50} showCount />
        </Form.Item>

        <Form.Item label="프로필 사진" extra="비워두면 기본 이미지로 표시됩니다.">
          <Space align="center" size={16}>
            <Avatar size={64} src={imageUrl ?? undefined} icon={<UserOutlined />} />
            <Space direction="vertical" size={4}>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleUpload}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                  이미지 선택
                </Button>
              </Upload>
              {imageUrl && (
                <Button size="small" danger type="text" onClick={() => setImageUrl(null)}>
                  사진 제거
                </Button>
              )}
            </Space>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdminAuthorModal;
